import { ComplianceControlRowData } from '@nirvana/shared/dtos/types/compliance-reports/gz-console';
import { formatLargeNumber } from 'apps/webcomponents/src/utils/formatter.utils';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { html, TemplateResult } from 'lit';
import { ComplianceAppView } from './ComplianceAppView';
import { hideIfNoAccessToComplianceStandard } from '../../common/util';

export function controlRenderer(rowData: ComplianceControlRowData): TemplateResult {
  /**
   * APACENGDEV-767: if control has no control name,
   *  render the description instead (with a static tooltip).
   */
  if (rowData.item.controlDetails.name === rowData.item.sectionDetails.name) {
    return html`<gz-tooltip isellipsis="true" tooltipText="${translateFn('View full control name in side panel.')}">
      <span>${stripHtmlTags(rowData.item.controlDetails.description)}</span>
    </gz-tooltip>`;
  }

  return html`<span class="cell-label">${rowData.item.controlDetails.name}</span>`;
}

export function controlIDRenderer(rowData: ComplianceControlRowData): TemplateResult {
  return html`<span class="cell-label">${rowData.item.controlDetails.id}</span>`;
}

export function sectionRenderer(rowData: ComplianceControlRowData): TemplateResult {
  return html`<span class="cell-label">${rowData.item.sectionDetails.name}</span>`;
}

export function sectionIDRenderer(rowData: ComplianceControlRowData): TemplateResult {
  return html`<span class="cell-label">${rowData.item.sectionDetails.id}</span>`;
}

export function scoringRenderer(view: ComplianceAppView, rowData: ComplianceControlRowData): TemplateResult {
  const { passedPercentage, checksResults, totalChecks } = rowData.item.controlDetails.checksStats;

  const score = updateScoreIfNoAccessToComplianceStandard(view, passedPercentage as number);
  const isNotEvaluated = totalChecks === checksResults.ignored;

  return html`<div class="w-full">
    <div class="d-flex w-80">
      <div class="w-75 d-flex align-items-center">
        <gz-progress-thin
          class="w-90 d-flex align-items-center justify-center"
          .valueNow=${score}
          valueMax="100"
          progressColor=${getRangeColor(score)}
        ></gz-progress-thin>
      </div>
      <div class="w-25 d-flex justify-space-between">
        ${hideIfNoAccessToComplianceStandard(view.dom.complianceOverview.$content.hasAccessToComplianceStandard, scorePercentageRenderer(isNotEvaluated, score))}
      </div>
    </div>
  </div>`;
}

/**
 * Removes all HTML tags from a string.
 * @param input The string to sanitize.
 * @returns The string with all HTML tags removed.
 */
export function stripHtmlTags(input: string): string {
  return input.replace(/<[^>]*>/g, '');
}

function getRangeColor(percentage: number): string {
  if (percentage >= 0 && percentage < 20) {
    return 'var(--red-chart-600)';
  } else if (percentage > 20 && percentage <= 70) {
    return 'var(--yellow-chart-050)';
  } else {
    return 'var(--green-layout-100-primary-green)';
  }
}

function updateScoreIfNoAccessToComplianceStandard(view: ComplianceAppView, score: number) {
  return view.dom.complianceOverview.$content.hasAccessToComplianceStandard ? score : 0;
}

// refactor(dhipolito): Remove redundancies
function scorePercentageRenderer(isNotEvaluated: boolean, percentage: number): TemplateResult | undefined {
  if (isNotEvaluated) {
    return html`<gz-text fontSize="13" style="color:var(--chart-302-icon-buttons-titles)"
      >${translateFn('Not evaluated')}</gz-text
    >`;
  }

  if (percentage === 100) {
    return html`
      <span class="text-medium fs-13 d-flex justify-center align-items-center">${percentage}%</span>
      <gz-icon color="var(--green-layout-100-primary-green)" size="20" name="ds-icon-notification_success"></gz-icon>
    `;
  } else if (percentage === 0) {
    return html`
      <span class="text-medium fs-13 d-flex justify-center align-items-center">${percentage}%</span>
      <gz-icon color="var(--red-layout-300-primary)" size="20" name="ds-icon-delete_large"></gz-icon>
    `;
  } else {
    return html`<span class="text-medium fs-13 d-flex justify-center align-items-center">${percentage}%</span> `;
  }
}

export function compliantCountRenderer(view: ComplianceAppView, rowData: ComplianceControlRowData): TemplateResult {
  return html` <span class="cell-label">
    ${hideIfNoAccessToComplianceStandard(view.dom.complianceOverview.$content.hasAccessToComplianceStandard, formatLargeNumber(rowData.item.controlDetails.checksStats.checksResults.compliant))}
  </span>`;
}

export function nonCompliantCountRenderer(view: ComplianceAppView, rowData: ComplianceControlRowData): TemplateResult {
  return html` <span class="cell-label">
    ${hideIfNoAccessToComplianceStandard(view.dom.complianceOverview.$content.hasAccessToComplianceStandard, formatLargeNumber(rowData.item.controlDetails.checksStats.checksResults.nonCompliant))}
  </span>`;
}

export function ignoredCountRenderer(view: ComplianceAppView, rowData: ComplianceControlRowData): TemplateResult {
  return html` <span class="cell-label">
    ${hideIfNoAccessToComplianceStandard(view.dom.complianceOverview.$content.hasAccessToComplianceStandard, formatLargeNumber(rowData.item.controlDetails.checksStats.checksResults.ignored))}
  </span>`;
}
